// Imports for external libraries go here.
import clsx from 'clsx';
import { FC, memo, useEffect } from 'react';

// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { Container } from '@adobe/aem-react-editable-components';
import { useClientEnvVarsStore, useUserDetailsGlobalStore } from '@marriott/mi-store-utils';
import { setupLinkInterception, dispatchCustomEvent } from '@marriott/shared/mi-helper-utils';
import { Icon, useCheckBreakpoint } from '@marriott/mi-ui-library';
import { FEATURE_FLAGS } from '@marriott/mi-feature-flag-utils';
import { clearSessionTimeout, initializeSessionTimeout } from '../../../utils/sessionTimeoutHelper';
import { SignInDrawerWrapper } from '../../SignInDrawer/SignInDrawerWrapper';
import { LanguageSelectorWrapper } from '../LanguageSelector/LanguageSelectorWrapper';
import { MyTrips } from '../MyTrips';
import { UtilityLink } from '../UtilityLink';
import { StyledNavigationUtilityLink } from './NavigationUtilityLink.styles';
import { NavigationUtilityLinkProps, RestPropType, UtilityChildModel } from './NavigationUtilityLink.types';
import { getIconClass, isLongTextLocale } from './helper';

import { useNavContext } from '../../../organisms/NavigationContainer';
import { SIGN_IN_LINK_SUFFIX } from '../../../utils/constants';

// Memoized container for the account flyout datapagecomponent.
// Prevents header re-renders (scroll, hover, sticky) from cascading into
// the FlyoutDrawer tree, which causes mobile keyboard flickering.
const DataPageContainer = memo<{
  utilityNavigationItem: Record<string, unknown>;
  cqPath: string | undefined;
  itemKey: string;
  isAuthorMode: boolean;
}>(({ utilityNavigationItem, cqPath, itemKey, isAuthorMode }) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { ':type': _type, ...restProps } = utilityNavigationItem;
  const utilityItemPath = `${cqPath}/${itemKey}`;
  const childItemKey = (restProps as Record<string, unknown>)?.[':itemsOrder'] as string[] | undefined;
  const firstChildKey = childItemKey?.[0];
  const items = (restProps as Record<string, Record<string, unknown>>)?.[':items'];
  let childModel: UtilityChildModel | undefined = undefined;
  if (firstChildKey && items && items[firstChildKey]) {
    const childItem = items[firstChildKey] as Record<string, unknown>;
    childModel = {
      ...childItem,
      cqItems: childItem[':items'],
      cqItemsOrder: childItem[':itemsOrder'],
    } as UtilityChildModel;
  }

  const modelCqItems = firstChildKey && childModel ? { [firstChildKey]: childModel } : {};
  const modelCqItemsOrder = firstChildKey && childModel ? [firstChildKey] : [];

  const containerProps = {
    cqPath: utilityItemPath,
    cqItemsOrder: childItemKey,
    cqType: 'mi-aem-account/components/content/datapagecomponent',
    model: {
      ':type': 'mi-aem-account/components/content/datapagecomponent',
      id: itemKey || 'datapagecomponent',
      ':path': utilityItemPath,
      cqItems: modelCqItems,
      cqType: 'mi-aem-account/components/content/datapagecomponent',
      cqItemsOrder: modelCqItemsOrder,
    },
  };

  return (
    <Container
      {...containerProps}
      model={containerProps?.model}
      isInEditor={!!isAuthorMode}
      isPage={false}
    />
  );
});

export const NavigationUtilityLink: FC<NavigationUtilityLinkProps> = props => {
  const {
    utilityNavigation,
    className,
    isRenderedInsideOverlay,
    logoUrl = '',
    logoImageAltText = '',
    logoType = '',
    cqPath,
    isAuthorMode,
    isBrandedVariation,
    featureFlagLegacyNav,
  } = props;
  const { ':itemsOrder': itemsOrder, ':items': items } = utilityNavigation;
  const { envVarsObject } = useClientEnvVarsStore();
  const locale = envVarsObject?.['ACCEPT_LANGUAGE'];
  const shouldOmitUtilityLabels = isLongTextLocale(locale) || isBrandedVariation;
  const isLegacyNavFlagEnabled = featureFlagLegacyNav?.[FEATURE_FLAGS.EXPERIENCE_MCOM_LEGACY_NAV];
  const { setIsSignInDrawerOpen } = useNavContext();

  const navigationLinkResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/navigation-link'
    : 'mi-aem-common-spa/components/content/globalnav/navigation-link';

  const languageSelectorResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/language-selector'
    : 'mi-aem-common-spa/components/content/globalnav/language-selector';

  const myTripsResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/mytrips'
    : 'mi-aem-common-spa/components/content/globalnav/mytrips';

  const signInResourceType = isLegacyNavFlagEnabled
    ? 'mi-aem-homepage-spa/components/content/signin'
    : 'mi-aem-common-spa/components/content/globalnav/signin';

  const isRememberedUser = useUserDetailsGlobalStore(state => state.isRememberMe);
  const isSignedInUser = useUserDetailsGlobalStore(state => state.isSignedInUser);
  const isAccountFlyoutEnabled = utilityNavigation?.resourceProperties?.['enableAccountFlyout'] === 'true';

  useEffect(() => {
    const triggerFlyoutOnLoad = () => {
      const urlParams = new URLSearchParams(window?.location?.search);
      const modalId = urlParams.get('modalId');
      const pathname = window?.location?.pathname;

      if (!modalId) return;

      // TODO : Need to revisit later when logout redirection query param is removed
      const shouldOpenInEarnOnly = ['logout', 'sessionTimedOut'].includes(modalId);
      const shouldDispatch = !shouldOpenInEarnOnly || (!isSignedInUser && pathname?.endsWith('earn.mi'));

      if (shouldDispatch) {
        dispatchCustomEvent(false, modalId);
      }
      setIsSignInDrawerOpen(true);
    };
    window.addEventListener('load', triggerFlyoutOnLoad);
    return () => window.removeEventListener('load', triggerFlyoutOnLoad);
  }, [isSignedInUser, setIsSignInDrawerOpen]);

  const isDesktopAndAbove = useCheckBreakpoint('viewportL');

  const renderUtilityLink = (
    restProps: RestPropType,
    defaultIconClass: string,
    buttonText?: string,
    buttonType?: string
  ) => {
    const { labelTag: iconClassTag, 'cq:panelUrl': href, openInaNewTab, 'cq:panelTitle': linkText } = restProps;

    //TODO: Keeping the fallback class as globe as of now as currentlty no option from AEM to author icon. Later should be changed.
    const iconClass = getIconClass(iconClassTag || defaultIconClass);
    const target = openInaNewTab === 'true' ? '_blank' : '_self';
    const rel = openInaNewTab === 'true' ? 'noopener noreferrer' : undefined;
    return (
      <>
        <Icon
          iconClass={`icon-${iconClass} utility-icon custom_click_track`}
          ariaLabel={restProps['cq:panelTitle']}
          iconHref={href}
          target={target}
          rel={rel}
          enableLinkClsName
          isDecorative={true}
        >
          {href && (!shouldOmitUtilityLabels || buttonType === 'signin') && (
            <span
              aria-hidden="true"
              className={clsx('utility-link-text', {
                'd-none d-xl-inline-block': !isRenderedInsideOverlay,
              })}
            >
              {linkText || buttonText}
            </span>
          )}
        </Icon>
        {!href && (!shouldOmitUtilityLabels || buttonType === 'signin') && (
          <span
            aria-hidden="true"
            className={clsx('utility-link-text', {
              'd-none d-xl-inline-block': !isRenderedInsideOverlay,
              'd-lg-inline-block': buttonType === 'signin',
            })}
          >
            {linkText || buttonText}
          </span>
        )}
      </>
    );
  };

  useEffect(() => {
    initializeSessionTimeout(utilityNavigation?.resourceProperties?.['sessionTimeOutTimer']);

    return () => {
      clearSessionTimeout();
    };
  }, []);

  useEffect(() => {
    const { interceptAnchorClicks } = setupLinkInterception(SIGN_IN_LINK_SUFFIX, () => {
      dispatchCustomEvent(!isRememberedUser);
      setIsSignInDrawerOpen(true);
    });
    const cleanupFn = isAccountFlyoutEnabled && interceptAnchorClicks();
    return () => {
      if (cleanupFn) cleanupFn();
    };
  }, [isAccountFlyoutEnabled, isRememberedUser]);

  return (
    <StyledNavigationUtilityLink
      className={clsx(className, 'pr-3 pr-md-5 pr-lg-0', { 'align-items-baseline': isAuthorMode })}
      isRenderedInsideOverLay={isRenderedInsideOverlay}
    >
      {/* this svg should be removed once the logo is put into the design system */}
      {!isDesktopAndAbove && isBrandedVariation && (
        <a href="/default.mi">
          <div className="brand-logo-marriott-bonvoy-wordmark" aria-label="Marriott Bonvoy" />
        </a>
      )}
      {itemsOrder.map((key: string) => {
        const utilityNavigationItem = items[key];

        const { ':type': type, ...restProps } = utilityNavigationItem;
        const trackingContentPosition = items?.['trackingProperties']?.trackingContentPosition;

        switch (type) {
          case navigationLinkResourceType:
            return (
              <li
                key={restProps['cq:panelTitle']}
                className={clsx(
                  'utility-item',
                  'utility-link',
                  { 't-label-inverse-xs d-none d-lg-block': !isRenderedInsideOverlay },
                  { 't-label-alt-xs': isRenderedInsideOverlay }
                )}
              >
                <UtilityLink
                  restProps={{ trackingContentPosition, ...restProps }}
                  defaultIconClass="help"
                  isBrandedVariation={isBrandedVariation}
                  isRenderedInsideOverlay={isRenderedInsideOverlay}
                />
              </li>
            ); //later a seperate component for utility link can be created: <UtilityLink {...restProps} />; //Logic for Links such as help, covid-19

          case languageSelectorResourceType:
            return (
              <li
                key={restProps['cq:panelTitle'] || restProps?.resourceProperties['cq:panelTitle']}
                className={clsx(
                  'utility-item',
                  'utility-language-selector',
                  { 't-label-inverse-xs d-none d-lg-block': !isRenderedInsideOverlay },
                  { 't-label-alt-xs': isRenderedInsideOverlay }
                )}
              >
                <LanguageSelectorWrapper
                  restProps={{ trackingContentPosition, ...restProps }}
                  renderUtilityLink={renderUtilityLink}
                  buttonClassName="t-label-inverse-xs"
                  cqPath={`${cqPath}/${key}`}
                  isAuthorMode={isAuthorMode}
                />
              </li>
            );

          case myTripsResourceType:
            return !isRenderedInsideOverlay ? (
              <li key={restProps['cq:panelTitle']} className={clsx('utility-item', 'utility-trips')}>
                <MyTrips trackingContentPosition={trackingContentPosition} restProps={restProps}>
                  {renderUtilityLink(restProps, 'nav---trips')}
                </MyTrips>
              </li>
            ) : null; //later a seperate component for mytrips can be created: <MyTrips {...restProps} />; //Logic for Trips

          case signInResourceType:
            return !isRenderedInsideOverlay ? (
              <>
                {/* this svg should be removed once the logo is put into the design system */}
                {isDesktopAndAbove && isBrandedVariation && (
                  <a href="/default.mi">
                    <div className="brand-logo-marriott-bonvoy-wordmark" aria-label="Marriott Bonvoy" />
                  </a>
                )}
                <li key={'signinbutton'} className={clsx('utility-item', 'utility-sigin')}>
                  <SignInDrawerWrapper
                    logoUrl={logoUrl}
                    logoImageAltText={logoImageAltText}
                    logoType={logoType}
                    restProps={restProps}
                    renderUtilityLink={renderUtilityLink}
                    cqPath={`${cqPath}/${key}`}
                    isAuthorMode={isAuthorMode}
                    isAccountFlyout={isAccountFlyoutEnabled}
                  />
                </li>
              </>
            ) : null;

          // Accept both datapagecomponent and global-header-sign-in variants
          case 'mi-aem-account/components/content/datapagecomponent': {
            // Render the Container if not inside overlay and (account flyout is enabled OR in author mode)
            return !isRenderedInsideOverlay && isAccountFlyoutEnabled ? (
              <li key={key} id="utility-datapagecomponent" className={clsx('utility-datapagecomponent')}>
                <DataPageContainer
                  utilityNavigationItem={utilityNavigationItem}
                  cqPath={cqPath}
                  itemKey={key}
                  isAuthorMode={!!isAuthorMode}
                />
              </li>
            ) : null;
          }

          default:
            return null;
        }
      })}
    </StyledNavigationUtilityLink>
  );
};
 